/**
 * Admin — API Monitor
 *
 * Real-time system health dashboard showing:
 *   - Overall status banner with uptime indicator
 *   - Endpoint health cards with latency bars and status codes
 *   - Data source table (where the app is sourcing its data from)
 *   - Auto-refresh with manual refresh option
 *   - Historical response times (in-memory, current session)
 *
 * Consumes the existing apiHealthService (useApiHealth, refreshHealth)
 * and ServiceStatus type from ApiStatusModal.
 *
 * English only — system admin UI.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  useApiHealth,
  refreshHealth,
  type EndpointHealth,
  type EndpointGroup,
  type HealthSnapshot,
} from '../../api/apiHealthService';
import { API_BASE_URL } from '../../api/client';
import type { ServiceStatus } from '../../Components/ApiStatusModal/ApiStatusModal';
import styles from './AdminApiMonitor.module.css';

// ── Constants ────────────────────────────────────────────────
const LATENCY_GOOD_MS = 500;
const LATENCY_WARN_MS = 1500;
const LATENCY_BAD_MS = 3000;
const MAX_HISTORY = 30; // data points per endpoint

// ── Types ────────────────────────────────────────────────────
type LatencyHistory = Record<string, number[]>;

// ── Helpers ──────────────────────────────────────────────────

function formatMs(ms: number | null): string {
  if (ms == null) return '--';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatTime(epoch: number | null): string {
  if (!epoch) return '--';
  return new Date(epoch).toLocaleTimeString();
}

function latencyColor(ms: number | null): string {
  if (ms == null) return '#9ca3af';
  if (ms <= LATENCY_GOOD_MS) return '#22c55e';
  if (ms <= LATENCY_WARN_MS) return '#f59e0b';
  return '#ef4444';
}

function latencyPercent(ms: number | null): number {
  if (ms == null) return 0;
  return Math.min(100, (ms / LATENCY_BAD_MS) * 100);
}

function statusIcon(status: EndpointHealth['status']): string {
  switch (status) {
    case 'up': return '\u25CF';       // filled circle
    case 'degraded': return '\u25CE'; // bullseye
    case 'down': return '\u25CF';     // filled circle (red)
    case 'checking': return '\u25CB'; // open circle
    default: return '\u25CB';
  }
}

function statusColor(status: EndpointHealth['status']): string {
  switch (status) {
    case 'up': return '#22c55e';
    case 'degraded': return '#f59e0b';
    case 'down': return '#ef4444';
    default: return '#9ca3af';
  }
}

function statusLabel(status: EndpointHealth['status']): string {
  switch (status) {
    case 'up': return 'Healthy';
    case 'degraded': return 'Degraded';
    case 'down': return 'Down';
    case 'checking': return 'Checking';
    default: return 'Unknown';
  }
}

function overallStatusConfig(status: HealthSnapshot['overallStatus']) {
  switch (status) {
    case 'healthy':
      return { label: 'All Systems Operational', color: '#22c55e', bg: '#f0fdf4', border: '#bbf7d0' };
    case 'degraded':
      return { label: 'Degraded Performance', color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' };
    case 'down':
      return { label: 'Service Disruption', color: '#ef4444', bg: '#fef2f2', border: '#fecaca' };
    case 'checking':
      return { label: 'Checking Services...', color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' };
  }
}

function sourceColor(source: ServiceStatus['source']): string {
  switch (source) {
    case 'api': return '#22c55e';
    case 'seed': return '#f59e0b';
    case 'ls': return '#3b82f6';
    case 'loading': return '#9ca3af';
    default: return '#9ca3af';
  }
}

function sourceLabel(source: ServiceStatus['source'], loading: boolean): string {
  if (loading) return 'Loading';
  switch (source) {
    case 'api': return 'Live API';
    case 'seed': return 'Seed (Offline)';
    case 'ls': return 'localStorage';
    case 'none': return 'Fallback';
    default: return '--';
  }
}

// ── Sparkline (mini latency chart) ───────────────────────────

function Sparkline({ data, width = 120, height = 28 }: { data: number[]; width?: number; height?: number }) {
  if (data.length < 2) return null;

  const max = Math.max(...data, LATENCY_GOOD_MS);
  const step = width / (data.length - 1);

  const points = data.map((v, i) => `${i * step},${height - (v / max) * (height - 4) - 2}`).join(' ');

  const lastVal = data[data.length - 1];
  const color = latencyColor(lastVal);

  return (
    <svg width={width} height={height} className={styles.sparkline}>
      {/* Threshold line */}
      <line
        x1={0}
        y1={height - (LATENCY_GOOD_MS / max) * (height - 4) - 2}
        x2={width}
        y2={height - (LATENCY_GOOD_MS / max) * (height - 4) - 2}
        stroke="#e5e7eb"
        strokeWidth={1}
        strokeDasharray="3,3"
      />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Current value dot */}
      <circle
        cx={(data.length - 1) * step}
        cy={height - (lastVal / max) * (height - 4) - 2}
        r={2.5}
        fill={color}
      />
    </svg>
  );
}

// ── Overall Status Banner ────────────────────────────────────

function StatusBanner({ health }: { health: HealthSnapshot }) {
  const config = overallStatusConfig(health.overallStatus);
  const upCount = health.endpoints.filter((e) => e.status === 'up').length;
  const total = health.endpoints.length;

  return (
    <div
      className={styles.banner}
      style={{
        background: config.bg,
        borderColor: config.border,
      }}
    >
      <div className={styles.bannerLeft}>
        <span className={styles.bannerDot} style={{ background: config.color }} />
        <div>
          <div className={styles.bannerTitle} style={{ color: config.color }}>
            {config.label}
          </div>
          <div className={styles.bannerMeta}>
            {upCount}/{total} endpoints responding
            {health.lastPollAt && (
              <> &middot; Last poll: {formatTime(health.lastPollAt)}</>
            )}
          </div>
        </div>
      </div>
      <div className={styles.bannerRight}>
        <div className={styles.bannerUrl}>{API_BASE_URL}</div>
      </div>
    </div>
  );
}

// ── Endpoint Card ────────────────────────────────────────────

function EndpointCard({ ep, history }: { ep: EndpointHealth; history: number[] }) {
  const color = statusColor(ep.status);
  const pct = latencyPercent(ep.responseMs);

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.cardName}>
          <span style={{ color }} className={styles.cardDot}>{statusIcon(ep.status)}</span>
          {ep.name}
        </div>
        <span className={styles.cardStatus} style={{ color }}>
          {statusLabel(ep.status)}
        </span>
      </div>

      <div className={styles.cardBody}>
        {/* Latency bar */}
        <div className={styles.latencyRow}>
          <span className={styles.latencyLabel}>Latency</span>
          <span className={styles.latencyValue} style={{ color: latencyColor(ep.responseMs) }}>
            {formatMs(ep.responseMs)}
          </span>
        </div>
        <div className={styles.latencyBarTrack}>
          <div
            className={styles.latencyBarFill}
            style={{
              width: `${pct}%`,
              background: latencyColor(ep.responseMs),
            }}
          />
          {/* Threshold markers */}
          <div
            className={styles.latencyThreshold}
            style={{ insetInlineStart: `${(LATENCY_GOOD_MS / LATENCY_BAD_MS) * 100}%` }}
            title="Good threshold (500ms)"
          />
          <div
            className={styles.latencyThreshold}
            style={{ insetInlineStart: `${(LATENCY_WARN_MS / LATENCY_BAD_MS) * 100}%` }}
            title="Warning threshold (1500ms)"
          />
        </div>

        {/* Details row */}
        <div className={styles.cardDetails}>
          <div className={styles.cardDetail}>
            <span className={styles.cardDetailLabel}>HTTP</span>
            <span className={styles.cardDetailValue}>{ep.statusCode ?? '--'}</span>
          </div>
          <div className={styles.cardDetail}>
            <span className={styles.cardDetailLabel}>Path</span>
            <span className={styles.cardDetailValue} title={ep.path}>{ep.path}</span>
          </div>
          <div className={styles.cardDetail}>
            <span className={styles.cardDetailLabel}>Checked</span>
            <span className={styles.cardDetailValue}>{formatTime(ep.lastChecked)}</span>
          </div>
        </div>

        {/* Sparkline */}
        {history.length >= 2 && (
          <div className={styles.cardSparkWrap}>
            <span className={styles.cardDetailLabel}>Response History</span>
            <Sparkline data={history} width={160} height={24} />
          </div>
        )}

        {/* Error message */}
        {ep.error && (
          <div className={styles.cardError}>{ep.error}</div>
        )}
      </div>
    </div>
  );
}

// ── Data Source Row ───────────────────────────────────────────

function DataSourceRow({ svc }: { svc: ServiceStatus }) {
  const color = sourceColor(svc.source);
  return (
    <tr>
      <td className={styles.dsName}>{svc.name}</td>
      <td>
        <span className={styles.dsBadge} style={{ color, borderColor: color }}>
          <span className={styles.dsDot} style={{ background: color }} />
          {sourceLabel(svc.source, svc.loading)}
        </span>
      </td>
      <td className={styles.dsCount}>
        {svc.loading
          ? '...'
          : svc.countLabel
            ? svc.countLabel
            : svc.count != null
              ? svc.count.toLocaleString()
              : '--'}
      </td>
    </tr>
  );
}

// ── Main Component ───────────────────────────────────────────

interface AdminApiMonitorProps {
  services: ServiceStatus[];
  onRefresh: () => void;
}

export default function AdminApiMonitor({ services, onRefresh }: AdminApiMonitorProps) {
  const health = useApiHealth();
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [historyMap, setHistoryMap] = useState<LatencyHistory>({});
  const prevPollRef = useRef<number | null>(null);

  // ── Track latency history ──
  useEffect(() => {
    if (health.lastPollAt && health.lastPollAt !== prevPollRef.current) {
      prevPollRef.current = health.lastPollAt;
      setHistoryMap((prev) => {
        const next = { ...prev };
        for (const ep of health.endpoints) {
          if (ep.responseMs != null) {
            const arr = [...(next[ep.name] || []), ep.responseMs];
            next[ep.name] = arr.slice(-MAX_HISTORY);
          }
        }
        return next;
      });
    }
  }, [health.lastPollAt, health.endpoints]);

  const handleRefresh = useCallback(() => {
    refreshHealth();
    onRefresh();
  }, [onRefresh]);

  // ── Compute summary stats ──
  const endpoints = health.endpoints;
  const upCount = endpoints.filter((e) => e.status === 'up').length;
  const degradedCount = endpoints.filter((e) => e.status === 'degraded').length;
  const downCount = endpoints.filter((e) => e.status === 'down').length;
  const avgLatency = endpoints.reduce((sum, e) => sum + (e.responseMs || 0), 0) / (endpoints.length || 1);

  // ── Group endpoints by subsystem, sorted: problems first ──
  const STATUS_PRIORITY: Record<string, number> = { down: 0, degraded: 1, checking: 2, unknown: 3, up: 4 };
  const sortedEndpoints = [...endpoints].sort(
    (a, b) => (STATUS_PRIORITY[a.status] ?? 9) - (STATUS_PRIORITY[b.status] ?? 9),
  );
  const GROUP_ORDER: EndpointGroup[] = [
    'Core Platform', 'Identity & Access', 'Content & Data', 'Communication', 'Admin Services',
  ];
  const groupedEndpoints = GROUP_ORDER
    .map((group) => ({
      group,
      items: sortedEndpoints.filter((ep) => ep.group === group),
    }))
    .filter((g) => g.items.length > 0);

  const apiSources = services.filter((s) => s.source === 'api' && !s.loading).length;
  const totalSources = services.length;

  return (
    <div className={styles.monitor}>
      {/* ── Header ── */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>API Monitor</h1>
          <p className={styles.subtitle}>Real-time service health and data source status</p>
        </div>
        <div className={styles.controls}>
          <label className={styles.autoToggle}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh (30s)
          </label>
          <button className={styles.refreshBtn} onClick={handleRefresh}>
            Refresh Now
          </button>
        </div>
      </div>

      {/* ── Status Banner ── */}
      <StatusBanner health={health} />

      {/* ── Summary Stats ── */}
      <div className={styles.statsRow}>
        <div className={styles.stat}>
          <div className={styles.statValue} style={{ color: '#22c55e' }}>{upCount}</div>
          <div className={styles.statLabel}>Healthy</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statValue} style={{ color: '#f59e0b' }}>{degradedCount}</div>
          <div className={styles.statLabel}>Degraded</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statValue} style={{ color: '#ef4444' }}>{downCount}</div>
          <div className={styles.statLabel}>Down</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statValue}>{Math.round(avgLatency)}ms</div>
          <div className={styles.statLabel}>Avg Latency</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statValue}>{apiSources}/{totalSources}</div>
          <div className={styles.statLabel}>Live Sources</div>
        </div>
      </div>

      {/* ── Endpoint Cards (grouped by subsystem) ── */}
      <div className={styles.sectionLabel}>Endpoint Health</div>
      {groupedEndpoints.map(({ group, items }) => {
        const groupDown = items.filter((e) => e.status === 'down').length;
        const groupDegraded = items.filter((e) => e.status === 'degraded').length;
        const groupColor = groupDown > 0 ? '#ef4444' : groupDegraded > 0 ? '#f59e0b' : '#22c55e';
        return (
          <div key={group} style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%', background: groupColor, flexShrink: 0,
              }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#374151', letterSpacing: '0.02em' }}>
                {group}
              </span>
              <span style={{ fontSize: 11, color: '#9ca3af' }}>
                {items.filter((e) => e.status === 'up').length}/{items.length} healthy
              </span>
            </div>
            <div className={styles.cardGrid}>
              {items.map((ep) => (
                <EndpointCard
                  key={ep.name}
                  ep={ep}
                  history={historyMap[ep.name] || []}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* ── Data Sources Table ── */}
      <div className={styles.sectionLabel}>Data Sources</div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Service</th>
              <th>Source</th>
              <th>Records</th>
            </tr>
          </thead>
          <tbody>
            {services.map((svc) => (
              <DataSourceRow key={svc.name} svc={svc} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
